// Camera-preset intent handler. Drives XY / XZ / YZ / ISO / RESET snaps
// for the gnomon widget + palette commands. Distance is preserved (we
// orbit on the unit sphere around the current OrbitControls target),
// matching what users expect from a "viewpoint" toggle — the only
// preset that re-pins the target back to the dataset center is RESET.

import * as THREE from 'three';
import { params } from '@store/instance';
import { INTENTS } from '@store/intents';
import { CAMERA_DEFAULTS } from '@config/defaults';
import { physicalToThreeJS } from '@adapters/coordinates';
import { setCameraProjection } from '../cameraProjection';
import { cycleNavMode, setNavMode } from '../cameraModes';
import type { CameraPreset } from '@store/schema';
import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ViewerContext } from '../context';

// Duration of the double-click pan-to-center glide.
const FOCUS_DURATION_SEC = 0.45;
// Each double-click also dollies in by this factor (0.7 = 30% closer), clamped
// to controls.minDistance so repeated clicks bottom out gracefully.
const FOCUS_ZOOM_FACTOR = 0.7;

function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

const TARGET_DEFAULT = new THREE.Vector3(
    CAMERA_DEFAULTS.target.x,
    CAMERA_DEFAULTS.target.y,
    CAMERA_DEFAULTS.target.z,
);

const POSITION_DEFAULT = new THREE.Vector3(
    CAMERA_DEFAULTS.position.x,
    CAMERA_DEFAULTS.position.y,
    CAMERA_DEFAULTS.position.z,
);

const ISO_DIR = new THREE.Vector3(1, 1, 1).normalize();

function presetDir(preset: CameraPreset): THREE.Vector3 {
    switch (preset) {
        case 'XY':    return new THREE.Vector3(0, 0, 1);
        case 'XZ':    return new THREE.Vector3(0, 1, 0);
        case 'YZ':    return new THREE.Vector3(1, 0, 0);
        case 'ISO':   return ISO_DIR.clone();
        case 'RESET': return POSITION_DEFAULT.clone().sub(TARGET_DEFAULT).normalize();
    }
}

function presetUp(preset: CameraPreset): THREE.Vector3 {
    // XZ looks straight down the +Y axis; world-up "above" the viewer is +Z.
    // The other presets keep the conventional world-up (+Y).
    return preset === 'XZ' ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
}

export function register(store: Store<State>, viewer: ViewerContext): Array<() => void> {
    return [
        store.onIntent(INTENTS.CAMERA_PRESET, (p) => {
            if (!p) return;
            const camera = viewer.three.camera;
            const controls = viewer.three.controls;
            if (!camera || !controls) return;

            if (p.preset === 'RESET') {
                camera.position.copy(POSITION_DEFAULT);
                controls.target.copy(TARGET_DEFAULT);
                camera.up.set(0, 1, 0);
                // Orthographic framing is governed by zoom, not distance — reset
                // it to the default framing (zoom = 1) too.
                if (camera instanceof THREE.OrthographicCamera) {
                    camera.zoom = 1;
                    camera.updateProjectionMatrix();
                }
                controls.update();
                return;
            }

            // Preserve the current framing on a viewpoint snap: reposition along
            // the preset direction at the same orbit radius. For perspective this
            // holds the zoom; for orthographic, distance is irrelevant to apparent
            // size and zoom is left untouched — so framing is preserved either way.
            const radius = camera.position.distanceTo(controls.target);
            const dir = presetDir(p.preset);
            camera.position.copy(controls.target).addScaledVector(dir, radius);
            camera.up.copy(presetUp(p.preset));
            camera.lookAt(controls.target);
            controls.update();
        }),

        // Pan the orbit pivot to a physical point and dolly in a notch — the pivot
        // glides to the selection (landing at screen center, like a shift-pan)
        // while it steps a little closer. Perspective eases the orbit radius to
        // FOCUS_ZOOM_FACTOR × current (clamped to minDistance); orthographic eases
        // camera.zoom by 1/FOCUS_ZOOM_FACTOR (clamped to maxZoom), since distance
        // doesn't affect apparent size there. Eased over FOCUS_DURATION_SEC via a
        // self-contained rAF tween: the pivot moves by *incremental* deltas
        // (respects a concurrent user pan) and the camera follows along its *live*
        // view direction each frame (respects a concurrent orbit).
        store.onIntent(INTENTS.CAMERA_FOCUS_POINT, (p) => {
            if (!p) return;
            const camera = viewer.three.camera;
            const controls = viewer.three.controls;
            if (!camera || !controls) return;
            const isOrtho = camera instanceof THREE.OrthographicCamera;
            const goal = physicalToThreeJS(p.x, p.y, p.z);
            const totalDelta = goal.clone().sub(controls.target);
            const startRadius = camera.position.distanceTo(controls.target);
            // Perspective: ease distance. Orthographic: hold distance, ease zoom.
            const goalRadius = isOrtho
                ? startRadius
                : Math.max(controls.minDistance, startRadius * FOCUS_ZOOM_FACTOR);
            const radiusDelta = goalRadius - startRadius;
            const startZoom = camera.zoom;
            const goalZoom = isOrtho
                ? Math.min(controls.maxZoom, startZoom / FOCUS_ZOOM_FACTOR)
                : startZoom;
            const zoomDelta = goalZoom - startZoom;
            // Already centered and at the zoom floor/ceiling → nothing to animate.
            if (totalDelta.lengthSq() < 1e-9 && Math.abs(radiusDelta) < 1e-6 && Math.abs(zoomDelta) < 1e-6) return;
            let elapsed = 0;
            let last = performance.now();
            const appliedPan = new THREE.Vector3();
            const desiredPan = new THREE.Vector3();
            const stepMove = new THREE.Vector3();
            const dir = new THREE.Vector3();
            const step = (): void => {
                const now = performance.now();
                elapsed = Math.min(FOCUS_DURATION_SEC, elapsed + (now - last) / 1000);
                last = now;
                const eased = easeInOutCubic(elapsed / FOCUS_DURATION_SEC);
                // Incremental pivot pan toward the goal.
                desiredPan.copy(totalDelta).multiplyScalar(eased);
                stepMove.copy(desiredPan).sub(appliedPan);
                appliedPan.copy(desiredPan);
                controls.target.add(stepMove);
                // Re-place the camera along the live target→camera direction at the
                // eased radius (keeps the selection centered via lookAt), and ease
                // zoom for orthographic.
                dir.copy(camera.position).sub(controls.target);
                const len = dir.length();
                if (len > 1e-6) {
                    const radius = startRadius + radiusDelta * eased;
                    camera.position.copy(controls.target).addScaledVector(dir.multiplyScalar(1 / len), radius);
                }
                if (isOrtho) {
                    camera.zoom = startZoom + zoomDelta * eased;
                    camera.updateProjectionMatrix();
                }
                controls.update();
                if (elapsed < FOCUS_DURATION_SEC) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        }),

        // Swap projection (perspective ↔ orthographic), matching framing.
        store.onIntent(INTENTS.CAMERA_SET_PROJECTION, (p) => {
            if (!p || (p.mode !== 'perspective' && p.mode !== 'orthographic')) return;
            setCameraProjection(viewer, p.mode);
            params.cameraProjection = p.mode;
        }),

        // Navigation mode: orbit / turntable / fly (set or cycle).
        store.onIntent(INTENTS.CAMERA_SET_NAV_MODE, (p) => {
            if (!p) return;
            setNavMode(viewer, p.mode);
        }),
        store.onIntent(INTENTS.CAMERA_CYCLE_NAV_MODE, () => {
            cycleNavMode(viewer);
        }),
    ];
}
